import { Response } from '_metronic/helpers'

import { VAZIO } from './_formato'

/**
 * Contrato da fila de envio ao PEC e-SUS APS (E4c), consumido pela tela de acompanhamento (E5d).
 *
 * Espelha campo a campo as projeções do backend `EsusEnvioList` (linha da listagem) e
 * `EsusEnvioView` (detalhe). Ambas são declaradas lá como **contrato público desta tela**: mudar
 * campo no backend obriga a atualizar este arquivo.
 *
 * Nada de credencial do PEC trafega aqui — senha e contra-chave de `EsusConfiguracao` nunca saem
 * em JSON, por decisão registrada nas duas projeções.
 */

/**
 * Status do envio, pelo `name()` do enum `StatusEsusEnvio`.
 *
 * São exatamente 4 valores. `DESCARTADO` foi removido do enum em E4a e **não existe** — não
 * acrescente sem conferir o enum do backend.
 */
export type EsusEnvioStatus = 'PENDENTE' | 'ENVIANDO' | 'ENVIADO' | 'ERRO'

/**
 * Ficha do LEDI que o envio transporta, pelo `name()` do enum `TipoFichaEsus` (F7a; `'FP'` na F3 da
 * Ficha de Procedimentos).
 *
 * **Por que `'FAI'`/`'FCI'`/`'FP'` e não `'A'`/`'C'`/`'P'`:** o enum do backend declara `valor`
 * `"A"`/`"C"`/`"P"`, mas esse literal só alimenta o `StringEnumConverter`, que é
 * `AttributeConverter` de **JPA** (persistência e migration `V279`/`V291`). Para JSON,
 * `StringEnum.toMap()` tem o `@JsonValue` **comentado** (`StringEnum.java:21`) e não há módulo
 * Jackson customizado — logo Jackson usa o default, que é `name()`. Mesma forma do
 * `StatusEsusEnvio` (`"P"/"N"/"E"/"R"` no banco, `PENDENTE`/… no JSON), já validado com backend
 * real na E5d, e o `EsusEnvioFiltroDto.tipoFicha` documenta `example = "FCI"` — o query param faz o
 * binding pelo mesmo `name()`.
 *
 * O `'FP'` **não** é presumido: está provado no endpoint real por
 * `EsusEnvioAutorizacaoWebTest.detalheDaFichaDeProcedimentosSerializaOTipoComoNameDoEnum`, que
 * assere `$.tipoFicha == "FP"` e ainda reprova o vazamento de `"tipoFicha":"P"`.
 */
export type EsusEnvioTipoFicha = 'FAI' | 'FCI' | 'FP'

/** Linha da listagem (`EsusEnvioList`). Não traz `mensagemErro`: o texto do erro só vem no detalhe. */
export type EsusEnvioListItem = {
  id: number
  status: EsusEnvioStatus
  statusDescricao?: string
  /** Ficha transportada (F7a). Opcional no tipo porque envio antigo em cache pode não trazê-la. */
  tipoFicha?: EsusEnvioTipoFicha
  /**
   * `descricao` do enum, vinda da projeção: "Atendimento individual" / "Cadastro individual" /
   * "Procedimentos".
   */
  tipoFichaDescricao?: string | null
  uuidFicha?: string
  tentativas?: number
  /** Nulo enquanto o envio não foi transmitido — é o caso de tudo que está em PENDENTE/ERRO. */
  dhEnvio?: string | null
  /** Empresa dona do envio: a lista é combinada, então cada linha precisa se identificar. */
  empresaId?: number | null
  /** `nomeFantasia` da empresa, com fallback em `nome` (decidido na projeção do backend). */
  empresaNome?: string | null
  unidadeId?: number | null
  unidadeNome?: string | null
  /** Nulo em **toda** ficha de cadastro (`FCI`), por desenho da F7a: ela é por cidadão, não por atendimento. */
  atendimentoId?: number | null
  /** Número de protocolo do atendimento — exibido na lista no lugar do id (`atendimento.protocolo`). */
  atendimentoProtocolo?: string | null
  /** Data do atendimento (`atendimento.dataInicio`), mesmo campo do filtro de período. */
  atendimentoData?: string | null
  pacienteId?: number | null
  pacienteNome?: string | null
}

/**
 * Detalhe do envio (`EsusEnvioView`): os campos da linha mais o desfecho de erro.
 *
 * `mensagemErro` chega consolidada e **truncada** (`truncar(...)` em `EsusEnvioServiceImpl`), com um
 * erro por linha no formato `campo: correcao` — ver `_esusErro.ts`.
 */
export type EsusEnvioDetalhe = EsusEnvioListItem & {
  mensagemErro?: string | null
  /** Momento em que o registro volta à fila automática; nulo = fora da fila (E4b). */
  dhProximaTentativa?: string | null
}

/**
 * Os 8 parâmetros discretos de `EsusEnvioFiltroDto`. Todos opcionais e combináveis.
 *
 * `empresaId` é **filtro**, não escopo: o backend deriva as empresas visíveis do usuário autenticado
 * e usa este parâmetro apenas para estreitar esse conjunto. Mandar uma empresa fora do vínculo não
 * amplia nada — devolve página vazia.
 */
export type EsusEnvioFiltros = {
  empresaId?: number
  unidadeId?: number
  status?: EsusEnvioStatus
  /**
   * `YYYY-MM-DD` — o backend usa `@DateTimeFormat(ISO.DATE)`. Data de referência: a do
   * **atendimento** na FAI e a de **criação do envio** na FCI, que não tem atendimento (F7a).
   */
  dataInicio?: string
  /** `YYYY-MM-DD` — mesma data de referência do início do período. */
  dataFim?: string
  pacienteId?: number
  atendimentoId?: number
  /** Ausente devolve os três tipos de ficha (F7a; a de procedimentos entrou na F3). */
  tipoFicha?: EsusEnvioTipoFicha
}

/** Espelha o `PageResponse<EsusEnvioList>` do backend. */
export type EsusEnviosQueryResponse = Response<Array<EsusEnvioListItem>>

/** Rótulos pt-BR do seletor de status — lista fechada, na ordem do ciclo de vida do envio. */
export const OPCOES_STATUS_ENVIO: Array<{ label: string; value: EsusEnvioStatus }> = [
  { label: 'Pendente', value: 'PENDENTE' },
  { label: 'Enviando', value: 'ENVIANDO' },
  { label: 'Enviado', value: 'ENVIADO' },
  { label: 'Erro', value: 'ERRO' },
]

/**
 * Classe de badge por status, sempre por token do tema — nenhum hex novo (AC5).
 *
 * `badge-light-primary` está fora de propósito: E5a provou que essa família fica com texto
 * ilegível porque só `$primary` foi sobrescrito pela marca.
 */
export const CLASSE_BADGE_STATUS: Record<EsusEnvioStatus, string> = {
  PENDENTE: 'badge-light-warning',
  ENVIANDO: 'badge-light-info',
  ENVIADO: 'badge-light-success',
  ERRO: 'badge-light-danger',
}

/** Rótulo pt-BR do status, com fallback na descrição vinda da API. */
export const rotuloStatus = (envio: {
  status?: EsusEnvioStatus
  statusDescricao?: string
}): string => {
  const opcao = OPCOES_STATUS_ENVIO.find((item) => item.value === envio.status)
  return opcao?.label || envio.statusDescricao || envio.status || '—'
}

/**
 * Rótulos pt-BR do seletor de tipo de ficha — lista fechada, na ordem do enum do backend.
 *
 * Os rótulos repetem a `descricao` do `TipoFichaEsus` de propósito: é o texto que a operação já vê
 * no PEC e no relatório de produção.
 */
export const OPCOES_TIPO_FICHA: Array<{ label: string; value: EsusEnvioTipoFicha }> = [
  { label: 'Atendimento individual', value: 'FAI' },
  { label: 'Cadastro individual', value: 'FCI' },
  { label: 'Procedimentos', value: 'FP' },
]

/** Rótulo pt-BR do tipo de ficha, com fallback na descrição vinda da API. */
export const rotuloTipoFicha = (envio: {
  tipoFicha?: EsusEnvioTipoFicha
  tipoFichaDescricao?: string | null
}): string => {
  const opcao = OPCOES_TIPO_FICHA.find((item) => item.value === envio.tipoFicha)
  return opcao?.label || envio.tipoFichaDescricao || envio.tipoFicha || VAZIO
}
